import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  PlayCircle, Building2, BookOpen, Users, HelpCircle, CalendarDays,
  FileText, CheckCircle2, Eye,
} from 'lucide-react';

import Button from '../../../components/ui/Button';
import DocumentViewerModal from '../../../components/ui/DocumentViewerModal';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const IconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 145, 239, 0.10);
  color: ${({ theme }) => theme.colors.primary.main};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: space-between;
`;

const Title = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TypeLabel = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 2px;
`;

const ViewedTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.secondary.dark};
  white-space: nowrap;
`;

const Bodytext = styled.div`
  margin-top: 10px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  white-space: pre-wrap;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const Empty = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const typeIcon = (contentType) => {
  switch (contentType) {
    case 'welcome_video': return PlayCircle;
    case 'company_intro': return Building2;
    case 'culture_doc': return BookOpen;
    case 'team_card': return Users;
    case 'faq': return HelpCircle;
    case 'first_day_program': return CalendarDays;
    default: return FileText;
  }
};

export default function WelcomeContentTab({ items, onMarkViewed }) {
  const { t } = useTranslation();
  // { fileUrl, title } | null — açık olduğunda DocumentViewerModal'ı besler. Hiçbir yükleme akışı
  // bu alanları genel sınıfla etiketlemediği için dosya varsayılan olarak ÖZEL sınıfa düşer.
  // DİKKAT (2026-08-18): `mediaUrl` bu uçtan artık İMZALI gelir — mobilin ham
  // `Linking.openURL` çağrısı için sunucu tarafında imzalanıyor (bkz.
  // MyPreboardingDataAccess.GetMyWelcomeContent) ve web AYNI ucu kullanıyor. `managerVideoUrl`
  // ise hâlâ KAYITLI adrestir. İkisi de sorunsuz: DocumentViewerModal adresi `/v1/storage/view`e
  // yollar ve sunucu imzayı orada soyup kayıtlı adrese indirger (StorageSignedUrlNormalizer),
  // böylece FileAccessGuard doğru aileyi tanır ve HER AÇILIŞTA taze imza üretilir (imzalı adres
  // 15 dakikada ölür — listedeki imzayı doğrudan kullanmak kırık bağlantı üretirdi). Eskiden
  // `window.open` ile
  // doğrudan açılıyordu; artık DocumentViewerModal kendisi `/v1/storage/view` üzerinden çözer
  // (dört özlük-dosyası yüzeyiyle aynı desen; ürün sahibi kararı: belgeler ASLA yeni sekmede
  // açılmaz).
  const [viewing, setViewing] = useState(null);

  const list = (Array.isArray(items) ? items : [])
    .slice()
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

  const openMedia = (item) => {
    const url = item.mediaUrl || item.managerVideoUrl;
    if (!url) return;
    setViewing({ fileUrl: url, title: item.title });
    if (!item.viewedAt) onMarkViewed(item.id);
  };

  return (
    <Wrap>
      {list.length === 0 && <Empty>{t('myPreboarding.welcome.empty')}</Empty>}

      {list.map((item) => {
        const Icon = typeIcon(item.contentType);
        const hasMedia = !!(item.mediaUrl || item.managerVideoUrl);

        return (
          <Card key={item.id}>
            <Row>
              <IconWrap>
                <Icon size={20} />
              </IconWrap>
              <Body>
                <HeadRow>
                  <div>
                    <Title>{item.title}</Title>
                    <TypeLabel>
                      {t(`myPreboarding.welcome.type.${item.contentType}`, { defaultValue: item.contentType })}
                    </TypeLabel>
                  </div>
                  {item.viewedAt && (
                    <ViewedTag>
                      <CheckCircle2 size={14} />
                      {t('myPreboarding.welcome.viewed')}
                    </ViewedTag>
                  )}
                </HeadRow>

                {item.bodyMarkdown && <Bodytext>{item.bodyMarkdown}</Bodytext>}

                <Actions>
                  {hasMedia ? (
                    <Button variant="outline" onClick={() => openMedia(item)}>
                      <Eye size={15} style={{ marginRight: 6 }} />
                      {t('myPreboarding.welcome.action.open')}
                    </Button>
                  ) : (
                    !item.viewedAt && (
                      <Button variant="ghost" onClick={() => onMarkViewed(item.id)}>
                        {t('myPreboarding.welcome.action.markViewed')}
                      </Button>
                    )
                  )}
                </Actions>
              </Body>
            </Row>
          </Card>
        );
      })}

      <DocumentViewerModal
        open={!!viewing}
        fileUrl={viewing?.fileUrl}
        title={viewing?.title}
        onClose={() => setViewing(null)}
      />
    </Wrap>
  );
}
